import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Bank, Check, X, HandCoins } from "@phosphor-icons/react";

const STATUS = {
  requested: "bg-amber-500/15 text-amber-400",
  paid: "bg-emerald-500/15 text-emerald-400",
  rejected: "bg-red-500/15 text-red-400",
};

export default function AdminPayouts() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState("");
  const { data } = useQuery({ queryKey: ["admin-payouts"], queryFn: async () => (await api.get("/admin/payouts")).data });
  const payouts = data?.payouts || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-payouts"] });

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/admin/payouts/${id}/${action}`);
      toast.success(action === "approve" ? "Marked as paid" : "Rejected & refunded");
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-payouts">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Reseller Payouts</h1>
        <p className="mt-1 text-sm text-muted-foreground">Review commission withdrawal requests. Approving marks it paid; rejecting refunds the amount to the reseller's wallet.</p>
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/10 bg-card">
        <div className="grid grid-cols-12 gap-2 border-b border-white/10 px-4 py-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <div className="col-span-4">Reseller</div><div className="col-span-2 text-right">Amount</div>
          <div className="col-span-3">Method / detail</div><div className="col-span-1">Status</div><div className="col-span-2 text-right">Actions</div>
        </div>
        {payouts.map((p) => (
          <div key={p.id} data-testid={`payout-row-${p.id}`} className="grid grid-cols-12 items-center gap-2 border-b border-white/5 px-4 py-3 text-sm last:border-0">
            <div className="col-span-4 min-w-0">
              <p className="truncate font-medium">{p.reseller_name}</p>
              <p className="truncate text-xs text-muted-foreground">{p.reseller_email}</p>
            </div>
            <div className="col-span-2 text-right font-mono-metric font-semibold">{money(p.amount_cents)}</div>
            <div className="col-span-3 min-w-0">
              <p className="capitalize">{p.method}</p>
              <p className="truncate text-xs text-muted-foreground">{p.detail || "—"}</p>
            </div>
            <div className="col-span-1"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS[p.status] || "bg-white/10 text-muted-foreground"}`}>{p.status}</span></div>
            <div className="col-span-2 flex items-center justify-end gap-1.5">
              {p.status === "requested" ? (
                <>
                  <button data-testid={`payout-approve-${p.id}`} onClick={() => act(p.id, "approve")} disabled={busy === p.id}
                    className="flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-40"><Check size={12} weight="bold" /> Paid</button>
                  <button data-testid={`payout-reject-${p.id}`} onClick={() => act(p.id, "reject")} disabled={busy === p.id}
                    className="rounded-lg border border-white/10 p-1.5 text-red-400 hover:bg-red-500/10"><X size={14} /></button>
                </>
              ) : <span className="text-xs text-muted-foreground">—</span>}
            </div>
          </div>
        ))}
        {payouts.length === 0 && <p className="flex items-center justify-center gap-2 px-4 py-10 text-center text-sm text-muted-foreground"><HandCoins size={16} /> No payout requests yet.</p>}
      </div>
    </div>
  );
}
